import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  AbsoluteWakeScheduler,
  createPlayerSnapshot,
  decideBoundary,
  observeActualTime,
  toggleQueue,
  type PlayerSnapshot,
  type PlayerStatus,
} from '../player/engine'
import {
  findNaturalNextNode,
  findPlaybackNodeAtTime,
  validPlaybackNodes,
  type ProjectConfig,
  type ProjectNode,
} from '../lib/project'
import { WholeSongSegmentNavigator } from './WholeSongSegmentNavigator'

type YTPlayerState = -1 | 0 | 1 | 2 | 3 | 5

interface YTPlayer {
  destroy(): void
  getCurrentTime(): number
  getDuration(): number
  pauseVideo(): void
  playVideo(): void
  seekTo(seconds: number, allowSeekAhead: boolean): void
}

interface YTNamespace {
  Player: new (
    element: HTMLElement,
    options: {
      host?: string
      videoId: string
      playerVars: Record<string, string | number>
      events: {
        onReady: () => void
        onStateChange: (event: { data: YTPlayerState }) => void
        onError: (event: { data: number }) => void
        onAutoplayBlocked?: () => void
      }
    },
  ) => YTPlayer
}

declare global {
  interface Window {
    YT?: YTNamespace
    onYouTubeIframeAPIReady?: () => void
  }
}

let apiReady: Promise<YTNamespace> | null = null

function loadYouTubeApi() {
  apiReady ??= new Promise<YTNamespace>((resolve, reject) => {
    if (window.YT?.Player) return resolve(window.YT)
    const previous = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      previous?.()
      if (window.YT?.Player) resolve(window.YT)
      else reject(new Error('YouTube Player API 未載入'))
    }
    if (!document.querySelector('script[data-youtube-api]')) {
      const script = document.createElement('script')
      script.src = 'https://www.youtube.com/iframe_api'
      script.async = true
      script.dataset.youtubeApi = 'true'
      script.onerror = () => {
        apiReady = null
        reject(new Error('YouTube Player API 未載入'))
      }
      document.head.appendChild(script)
    }
  })
  return apiReady
}

const youtubeErrorMessage: Record<number, string> = {
  2: '影片 ID 不正確',
  5: '目前裝置無法播放此影片',
  100: '影片不存在或設為私人',
  101: '影片擁有者不允許嵌入播放',
  150: '影片擁有者不允許嵌入播放',
  153: '播放器來源設定不完整',
}
const thumbnailVariants = ['maxresdefault.jpg', 'sddefault.jpg', 'hqdefault.jpg'] as const
const FIXED_TIMING_MODE = { phaseCorrection: true, deviceCompensation: true } as const
const PREVIEW_MIN_SECONDS = 0.5
const PREVIEW_MAX_SECONDS = 10

type PreviewNode = Pick<ProjectNode, 'id' | 'label' | 'start'> & { end?: number | null }
type EditorPreviewRequest = (source: PreviewNode, candidate: PreviewNode, seconds: number, trigger: HTMLElement) => boolean
type TemporaryPreview =
  | { kind: 'pause'; end: number; restorePosition: number }
  | { kind: 'connection'; source: PreviewNode; candidate: PreviewNode; secondStart: number; secondEnd: number; phase: 1 | 2 }
type PreviewTransition = {
  token: number
  stage: 'pausing' | 'seeking' | 'starting' | 'playing' | 'transitioning' | 'restoring'
  start: number
  internalPausePending: boolean
}

const formatTime = (seconds: number) => {
  const minutes = Math.floor(Math.max(0, seconds) / 60)
  const remainder = (Math.max(0, seconds) % 60).toFixed(1).padStart(4, '0')
  return `${String(minutes).padStart(2, '0')}:${remainder}`
}

const quantizeTenths = (value: number) => Math.round(value * 10) / 10
const isPreviewSeconds = (value: string) => {
  const parsed = Number(value)
  return Number.isFinite(parsed)
    && parsed >= PREVIEW_MIN_SECONDS
    && parsed <= PREVIEW_MAX_SECONDS
    && Math.abs(parsed * 10 - Math.round(parsed * 10)) < 1e-8
}

export type PlayerPlaybackState = {
  currentNodeId: string | null
  playing: boolean
  switching: boolean
  status: PlayerStatus
  playbackIntent: boolean
}

export function InteractivePlayer({
  config,
  compact = false,
  onDurationReady,
  onPlaybackError,
  onCurrentTimeChange,
  onPlaybackStateChange,
  onEditorSelectionRequestReady,
  onConfirmedPausedPosition,
  onEditorBusyChange,
  onEditorPreviewRequestReady,
  onEditorPreviewReadyChange,
  onEditorPreviewStateChange,
  showNextChoices = true,
  shareLayout = false,
  onProgramEnd,
  programEndToken,
  autoAdvanceEnabled = true,
  autoStart = false,
  onImmersiveOpen,
  immersiveEnabled = false,
  immersiveActive = false,
  playlistPane,
  onActualPlay,
  onAutoplayBlocked,
}: {
  config: ProjectConfig
  compact?: boolean
  onDurationReady?: (duration: number) => void
  onPlaybackError?: (message: string) => void
  onCurrentTimeChange?: (time: number) => void
  onPlaybackStateChange?: (state: PlayerPlaybackState) => void
  onEditorSelectionRequestReady?: (request: ((nodeId: string) => void) | null) => void
  onConfirmedPausedPosition?: (seconds: number | null) => void
  onEditorBusyChange?: (busy: boolean) => void
  onEditorPreviewRequestReady?: (request: EditorPreviewRequest | null) => void
  onEditorPreviewReadyChange?: (ready: boolean) => void
  onEditorPreviewStateChange?: (state: { sourceLabel: string; candidateLabel: string; phase: 1 | 2 } | null) => void
  showNextChoices?: boolean
  shareLayout?: boolean
  /** Fired once by an engine final-stop or a validated YouTube physical-end fallback. */
  onProgramEnd?: (youtubeVideoId: string, autoAdvanceEnabled: boolean) => void
  /** Stable playlist-entry identity supplied by the host; defaults to the video's config ID. */
  programEndToken?: string
  /** The playlist session switch, read at final-stop without affecting paths. */
  autoAdvanceEnabled?: boolean
  /** Used by playlist auto-advance after a user-initiated playback session. */
  autoStart?: boolean
  onImmersiveOpen?: () => void
  immersiveEnabled?: boolean
  immersiveActive?: boolean
  playlistPane?: ReactNode
  /** Called once after a native YouTube play gesture is confirmed by PLAYING. */
  onActualPlay?: (youtubeVideoId: string, eventId: string) => void
  /** A programmatic playlist continuation needs an explicit viewer gesture. */
  onAutoplayBlocked?: () => void
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<YTPlayer | null>(null)
  const configRef = useRef(config)
  const snapshotRef = useRef<PlayerSnapshot>(createPlayerSnapshot(config))
  const schedulerRef = useRef<AbsoluteWakeScheduler | null>(null)
  const previewSchedulerRef = useRef<AbsoluteWakeScheduler | null>(null)
  const previewBoundaryHandlerRef = useRef<((actualTime: number) => boolean) | null>(null)
  const timingModeRef = useRef(FIXED_TIMING_MODE)
  const seekTimerRef = useRef<number | null>(null)
  const previewTimerRef = useRef<number | null>(null)
  const temporaryPreviewRef = useRef<TemporaryPreview | null>(null)
  const previewGenerationRef = useRef(0)
  const previewTransitionRef = useRef<PreviewTransition | null>(null)
  const temporarySeekingRef = useRef(false)
  const fineSeekTargetRef = useRef<number | null>(null)
  const previewFocusRef = useRef<HTMLElement | null>(null)
  const editorPreviewAcceptingRef = useRef(false)
  const generationRef = useRef(0)
  const firstPlayingRef = useRef(false)
  const userGestureRef = useRef(false)
  const gestureExpiryRef = useRef<number | null>(null)
  const playEntryAttemptedRef = useRef(false)
  const playEntryIdRef = useRef('')
  const onActualPlayRef = useRef(onActualPlay)
  const onAutoplayBlockedRef = useRef(onAutoplayBlocked)
  const onProgramEndRef = useRef(onProgramEnd)
  const programEndTokenRef = useRef(programEndToken ?? config.video.videoId)
  const autoAdvanceEnabledRef = useRef(autoAdvanceEnabled)
  const autoStartTimerRef = useRef<number | null>(null)
  // A later parent render must not recreate a playlist player's auto-start
  // intent; each mounted video may auto-start at most once.
  const autoStartOnMountRef = useRef(autoStart)
  const countdownAnnouncedRef = useRef(false)
  const programEndedRef = useRef(false)
  const [snapshot, setSnapshot] = useState(snapshotRef.current)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState('')
  const [lockedMessage, setLockedMessage] = useState('')
  const [thumbnailIndex, setThumbnailIndex] = useState(0)
  const [countdownAnnouncement, setCountdownAnnouncement] = useState('')
  const [confirmedPausedPosition, setConfirmedPausedPosition] = useState<number | null>(null)
  const [fineDraft, setFineDraft] = useState<number | null>(null)
  const [fineSeekTarget, setFineSeekTarget] = useState<number | null>(null)
  const [previewSeconds, setPreviewSeconds] = useState('5.0')
  const [previewSecondsTouched, setPreviewSecondsTouched] = useState(false)
  const [temporaryPreview, setTemporaryPreview] = useState<TemporaryPreview | null>(null)
  const [editorPreviewError, setEditorPreviewError] = useState('')
  const [editorPreviewStatus, setEditorPreviewStatus] = useState('')
  const [visitedNodeIds, setVisitedNodeIds] = useState<Set<string>>(() => new Set())

  useEffect(() => { onActualPlayRef.current = onActualPlay }, [onActualPlay])
  useEffect(() => { onAutoplayBlockedRef.current = onAutoplayBlocked }, [onAutoplayBlocked])
  // final-stop can happen between a parent state update and passive effects;
  // keep this callback current during render so the session switch is honored.
  onProgramEndRef.current = onProgramEnd
  programEndTokenRef.current = programEndToken ?? config.video.videoId
  autoAdvanceEnabledRef.current = autoAdvanceEnabled

  const nodes = useMemo(() => validPlaybackNodes(config.nodes, config.video.duration), [config.nodes, config.video.duration])
  const nodesById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes])
  const currentNode = snapshot.currentNodeId ? nodesById.get(snapshot.currentNodeId) ?? null : null
  const naturalNext = currentNode ? findNaturalNextNode(config.nodes, config.video.duration, currentNode.id) : null
  const choices = useMemo(() => {
    if (!currentNode) return []
    return config.edges
      .filter((edge) => edge.from === currentNode.id)
      .sort((a, b) => a.order - b.order)
      .map((edge) => ({ ...edge, node: nodesById.get(edge.to) }))
      .filter((choice): choice is typeof choice & { node: ProjectNode & { end: number } } => Boolean(choice.node))
  }, [config.edges, currentNode, nodesById])
  const thumbnails = useMemo(
    () => thumbnailVariants.map((name) => `https://i.ytimg.com/vi/${config.video.videoId}/${name}`),
    [config.video.videoId],
  )

  const commit = useCallback((next: PlayerSnapshot) => {
    snapshotRef.current = next
    setSnapshot(next)
  }, [])

  const clearSeekTimer = useCallback(() => {
    if (seekTimerRef.current !== null) window.clearTimeout(seekTimerRef.current)
    seekTimerRef.current = null
  }, [])

  const clearPreviewTimer = useCallback(() => {
    if (previewTimerRef.current !== null) window.clearTimeout(previewTimerRef.current)
    previewTimerRef.current = null
    previewSchedulerRef.current?.stop()
  }, [])

  const stopScheduler = useCallback((resetEstimate = false) => {
    schedulerRef.current?.stop(resetEstimate)
  }, [])

  const setConfirmedPosition = useCallback((value: number | null) => {
    const next = value === null ? null : quantizeTenths(value)
    setConfirmedPausedPosition(next)
    setFineDraft(next)
    onConfirmedPausedPosition?.(next)
  }, [onConfirmedPausedPosition])

  const clearTemporaryPreview = useCallback((restoreFocus = true) => {
    clearPreviewTimer()
    previewGenerationRef.current += 1
    previewTransitionRef.current = null
    temporaryPreviewRef.current = null
    temporarySeekingRef.current = false
    setTemporaryPreview(null)
    if (restoreFocus) window.setTimeout(() => previewFocusRef.current?.isConnected && previewFocusRef.current.focus(), 0)
  }, [clearPreviewTimer])

  const cancelTemporaryPreview = useCallback((restoreFocus = true) => {
    const wasConnectionPreview = temporaryPreviewRef.current?.kind === 'connection'
    clearTemporaryPreview(restoreFocus)
    if (wasConnectionPreview) setEditorPreviewStatus('試聽接法已取消。')
  }, [clearTemporaryPreview])

  const sampleActualTime = useCallback((actualTime: number, clearQueueOnCross = false) => {
    const next = observeActualTime(snapshotRef.current, configRef.current, actualTime, !clearQueueOnCross)
    commit(next)
    onCurrentTimeChange?.(actualTime)
    return next
  }, [commit, onCurrentTimeChange])

  const failEditorPreview = useCallback((message: string) => {
    playerRef.current?.pauseVideo()
    clearTemporaryPreview()
    setFineSeekTarget(null)
    fineSeekTargetRef.current = null
    setFineDraft(confirmedPausedPosition)
    setEditorPreviewError(message)
  }, [clearTemporaryPreview, confirmedPausedPosition])

  const verifyRawSeek = useCallback((target: number, previewToken: number, onConfirmed: () => void, attempt = 0) => {
    clearSeekTimer()
    const generation = generationRef.current
    const startedAt = performance.now()
    const check = () => {
      if (generation !== generationRef.current || previewToken !== previewGenerationRef.current || !playerRef.current) return
      const actual = playerRef.current.getCurrentTime()
      onCurrentTimeChange?.(actual)
      if (Math.abs(actual - target) <= configRef.current.playbackSettings.seekToleranceSeconds) {
        temporarySeekingRef.current = false
        onConfirmed()
        return
      }
      if (performance.now() - startedAt >= configRef.current.playbackSettings.seekTimeoutMs) {
        if (attempt === 0) {
          playerRef.current.seekTo(target, true)
          verifyRawSeek(target, previewToken, onConfirmed, 1)
          return
        }
        failEditorPreview(`無法定位至 ${formatTime(target)}，請再試一次。`)
        return
      }
      seekTimerRef.current = window.setTimeout(check, 100)
    }
    seekTimerRef.current = window.setTimeout(check, 100)
  }, [clearSeekTimer, failEditorPreview, onCurrentTimeChange])

  const finishTemporaryPreview = useCallback((position: number) => {
    const player = playerRef.current
    if (!player) return
    const completedPreview = temporaryPreviewRef.current
    clearPreviewTimer()
    const previewToken = ++previewGenerationRef.current
    previewTransitionRef.current = { token: previewToken, stage: 'restoring', start: position, internalPausePending: true }
    temporarySeekingRef.current = true
    player.pauseVideo()
    player.seekTo(position, true)
    verifyRawSeek(position, previewToken, () => {
      if (previewToken !== previewGenerationRef.current) return
      onCurrentTimeChange?.(position)
      commit({ ...snapshotRef.current, status: 'paused', playbackIntent: false, actualTime: position })
      setConfirmedPosition(position)
      clearTemporaryPreview()
      if (completedPreview?.kind === 'connection') setEditorPreviewStatus(`試聽接法完成，已停在 ${formatTime(position)}。`)
    })
  }, [clearPreviewTimer, clearTemporaryPreview, commit, onCurrentTimeChange, setConfirmedPosition, verifyRawSeek])

  const playPreviewRange = useCallback((start: number, end: number, preview: TemporaryPreview) => {
    const player = playerRef.current
    if (!player) return
    temporaryPreviewRef.current = preview
    setTemporaryPreview(preview)
    const previewToken = ++previewGenerationRef.current
    previewTransitionRef.current = { token: previewToken, stage: 'pausing', start, internalPausePending: true }
    temporarySeekingRef.current = true
    stopScheduler()
    clearPreviewTimer()
    clearSeekTimer()
    player.pauseVideo()
    if (previewToken !== previewGenerationRef.current || !previewTransitionRef.current) return
    previewTransitionRef.current.stage = 'seeking'
    player.seekTo(start, true)
    verifyRawSeek(start, previewToken, () => {
      if (!playerRef.current || !temporaryPreviewRef.current || previewToken !== previewGenerationRef.current) return
      const transition = previewTransitionRef.current
      if (!transition || transition.token !== previewToken || transition.stage !== 'seeking') return
      transition.stage = 'starting'
      commit({ ...snapshotRef.current, status: 'playing', playbackIntent: true, actualTime: start })
      playerRef.current.playVideo()
      previewSchedulerRef.current?.start(
        end - configRef.current.playbackSettings.endToleranceSeconds,
        true,
        timingModeRef.current,
      )
    })
  }, [clearPreviewTimer, clearSeekTimer, commit, stopScheduler, verifyRawSeek])

  const handlePreviewBoundary = useCallback((actualTime: number) => {
    const active = temporaryPreviewRef.current
    const activePlayer = playerRef.current
    const transition = previewTransitionRef.current
    if (!active || !activePlayer || !transition || transition.token !== previewGenerationRef.current || transition.stage !== 'playing') return false
    const end = active.kind === 'connection'
      ? active.phase === 1 ? active.source.end as number : active.secondEnd
      : active.end
    const boundary = end - configRef.current.playbackSettings.endToleranceSeconds
    if (actualTime < boundary) return false
    transition.stage = 'transitioning'
    if (active.kind === 'connection' && active.phase === 1) {
      const second = { ...active, phase: 2 as const }
      playPreviewRange(active.secondStart, active.secondEnd, second)
      return true
    }
    finishTemporaryPreview(active.kind === 'connection' ? end : active.restorePosition)
    return true
  }, [finishTemporaryPreview, playPreviewRange])

  useEffect(() => {
    previewBoundaryHandlerRef.current = handlePreviewBoundary
  }, [handlePreviewBoundary])

  const verifySeek = useCallback((target: ProjectNode & { end: number }, shouldPlay: boolean, attempt = 0) => {
    clearSeekTimer()
    const generation = generationRef.current
    const startedAt = performance.now()
    const check = () => {
      if (generation !== generationRef.current || !playerRef.current) return
      const actual = playerRef.current.getCurrentTime()
      sampleActualTime(actual)
      if (Math.abs(actual - target.start) <= configRef.current.playbackSettings.seekToleranceSeconds) {
        const status: PlayerStatus = shouldPlay ? 'playing' : 'paused'
        commit({ ...snapshotRef.current, status, playbackIntent: shouldPlay })
        if (shouldPlay) playerRef.current.playVideo()
        return
      }
      if (performance.now() - startedAt >= configRef.current.playbackSettings.seekTimeoutMs) {
        if (attempt === 0) {
          playerRef.current.seekTo(target.start, true)
          verifySeek(target, shouldPlay, 1)
          return
        }
        playerRef.current.pauseVideo()
        const message = `無法切換到「${target.label}」，請再點一次該段落`
        setError(message)
        commit({ ...snapshotRef.current, status: 'error', queuedNodeId: null, playbackIntent: false })
        return
      }
      seekTimerRef.current = window.setTimeout(check, 100)
    }
    seekTimerRef.current = window.setTimeout(check, 100)
  }, [clearSeekTimer, commit, sampleActualTime])

  const seekToNode = useCallback((nodeId: string, shouldPlay = true) => {
    const target = validPlaybackNodes(configRef.current.nodes, configRef.current.video.duration)
      .find((node) => node.id === nodeId)
    if (!target || !playerRef.current) return
    generationRef.current += 1
    stopScheduler()
    clearSeekTimer()
    setLockedMessage('')
    setError('')
    commit({
      ...snapshotRef.current,
      status: 'switching',
      currentNodeId: target.id,
      queuedNodeId: null,
      playbackIntent: shouldPlay,
    })
    playerRef.current.seekTo(target.start, true)
    onCurrentTimeChange?.(target.start)
    verifySeek(target, shouldPlay)
  }, [clearSeekTimer, commit, onCurrentTimeChange, stopScheduler, verifySeek])

  const switchToNodeNow = useCallback((nodeId: string) => {
    const state = snapshotRef.current
    if (!playerRef.current || (!ready && state.status !== 'autoplay-blocked')) return
    const shouldPlay = state.status === 'playing'
      || ((state.status === 'buffering' || state.status === 'switching') && state.playbackIntent)
      || state.status === 'cover'
      || state.status === 'ready'
      || state.status === 'ended'
      || state.status === 'autoplay-blocked'
    if (shouldPlay) {
      userGestureRef.current = true
      if (gestureExpiryRef.current !== null) window.clearTimeout(gestureExpiryRef.current)
      gestureExpiryRef.current = window.setTimeout(() => { userGestureRef.current = false }, 1600)
    }
    seekToNode(nodeId, shouldPlay)
  }, [ready, seekToNode])

  const emitProgramEndOnce = useCallback(() => {
    if (programEndedRef.current) return
    programEndedRef.current = true
    onProgramEndRef.current?.(programEndTokenRef.current, autoAdvanceEnabledRef.current)
  }, [])

  const finishProgram = useCallback((pausePlayer: boolean) => {
    if (pausePlayer) playerRef.current?.pauseVideo()
    stopScheduler()
    commit({ ...snapshotRef.current, status: 'ended', playbackIntent: false })
    emitProgramEndOnce()
  }, [commit, emitProgramEndOnce, stopScheduler])

  const handleBoundary = useCallback((actualTime: number) => {
    const decision = decideBoundary(snapshotRef.current, configRef.current, actualTime)
    if (decision.type === 'queued') {
      seekToNode(decision.targetNodeId, true)
      return true
    }
    if (decision.type === 'final-stop') {
      finishProgram(true)
      return true
    }
    return false
  }, [finishProgram, seekToNode])

  const handleProviderEnded = useCallback((actualTime: number) => {
    const player = playerRef.current
    const providerDuration = player?.getDuration() ?? 0
    const hasReachedPhysicalEnd = Number.isFinite(providerDuration) && providerDuration > 0
      && actualTime >= providerDuration - 1
    const mayCompleteProgram = firstPlayingRef.current
      && !temporaryPreviewRef.current
      && !temporarySeekingRef.current
      && fineSeekTargetRef.current === null

    // YouTube may announce ENDED before the scheduler receives a final sample;
    // this is especially common when a saved config end is slightly beyond the
    // provider's real duration. The provider's physical end is authoritative.
    if (mayCompleteProgram && hasReachedPhysicalEnd) {
      finishProgram(false)
      return
    }

    // An unexpected/non-physical ENDED is never a playlist completion. Let the
    // same engine authority recover a queued/natural segment when possible.
    const decision = decideBoundary(snapshotRef.current, configRef.current, actualTime)
    if (mayCompleteProgram && decision.type === 'queued') {
      seekToNode(decision.targetNodeId, true)
      return
    }
    if (mayCompleteProgram && decision.type === 'natural') {
      seekToNode(decision.nextNodeId, true)
      return
    }
    stopScheduler()
    commit({ ...snapshotRef.current, status: 'ended', playbackIntent: false })
  }, [commit, finishProgram, seekToNode, stopScheduler])

  const startScheduler = useCallback(() => {
    const player = playerRef.current
    if (!player || snapshotRef.current.status !== 'playing') return
    const actual = player.getCurrentTime()
    const current = findPlaybackNodeAtTime(
      configRef.current.nodes,
      configRef.current.video.duration,
      actual,
      snapshotRef.current.currentNodeId,
    )
    const boundary = current ? current.end - configRef.current.playbackSettings.endToleranceSeconds : null
    schedulerRef.current?.start(boundary, Boolean(snapshotRef.current.queuedNodeId), timingModeRef.current)
  }, [])

  useEffect(() => {
    configRef.current = config
    setVisitedNodeIds(new Set())
    if (temporaryPreviewRef.current || fineSeekTargetRef.current !== null) {
      fineSeekTargetRef.current = null
      setFineSeekTarget(null)
      cancelTemporaryPreview(false)
    }
    const actual = playerRef.current?.getCurrentTime() ?? snapshotRef.current.actualTime
    const observed = observeActualTime(snapshotRef.current, config, actual, false)
    commit(observed)
    generationRef.current += 1
    stopScheduler()
    if (observed.status === 'playing') window.queueMicrotask(startScheduler)
  }, [cancelTemporaryPreview, commit, config, startScheduler, stopScheduler])

  useEffect(() => {
    if (snapshot.status !== 'playing' || !snapshot.currentNodeId) return
    setVisitedNodeIds((previous) => {
      if (previous.has(snapshot.currentNodeId as string)) return previous
      const next = new Set(previous)
      next.add(snapshot.currentNodeId as string)
      return next
    })
  }, [snapshot.currentNodeId, snapshot.status])

  useEffect(() => {
    schedulerRef.current = new AbsoluteWakeScheduler({
      now: () => performance.now(),
      getActualTime: () => playerRef.current?.getCurrentTime() ?? snapshotRef.current.actualTime,
      onSample: (actual) => {
        const previousNodeId = snapshotRef.current.currentNodeId
        const next = sampleActualTime(actual, true)
        if (next.currentNodeId !== previousNodeId && snapshotRef.current.status === 'playing') {
          window.queueMicrotask(startScheduler)
        }
      },
      onBoundary: handleBoundary,
      isSampleValid: () => (
        document.visibilityState === 'visible'
        && snapshotRef.current.status === 'playing'
        && generationRef.current >= 0
      ),
      setTimer: (callback, delay) => window.setTimeout(callback, delay),
      clearTimer: (id) => window.clearTimeout(id),
    })
    previewSchedulerRef.current = new AbsoluteWakeScheduler({
      now: () => performance.now(),
      getActualTime: () => playerRef.current?.getCurrentTime() ?? snapshotRef.current.actualTime,
      onSample: (actual) => {
        const transition = previewTransitionRef.current
        if (
          transition?.stage === 'playing'
          && transition.internalPausePending
          && actual - transition.start > configRef.current.playbackSettings.seekToleranceSeconds
        ) transition.internalPausePending = false
        sampleActualTime(actual, true)
      },
      onBoundary: (actual) => previewBoundaryHandlerRef.current?.(actual) ?? false,
      isSampleValid: () => (
        document.visibilityState === 'visible'
        && snapshotRef.current.status === 'playing'
        && temporaryPreviewRef.current !== null
      ),
      setTimer: (callback, delay) => window.setTimeout(callback, delay),
      clearTimer: (id) => window.clearTimeout(id),
    })
    return () => {
      schedulerRef.current?.stop()
      schedulerRef.current = null
      previewSchedulerRef.current?.stop()
      previewSchedulerRef.current = null
    }
  }, [handleBoundary, sampleActualTime, startScheduler])

  useEffect(() => {
    if (snapshot.status !== 'paused' || temporaryPreviewRef.current || fineSeekTargetRef.current !== null) return
    let disposed = false
    let lastObserved = playerRef.current?.getCurrentTime() ?? snapshot.actualTime
    const pollPausedSeek = () => {
      if (disposed || snapshotRef.current.status !== 'paused' || temporaryPreviewRef.current || fineSeekTargetRef.current !== null) return
      const actual = playerRef.current?.getCurrentTime()
      if (actual !== undefined && Number.isFinite(actual) && Math.abs(actual - lastObserved) >= 0.05) {
        lastObserved = actual
        sampleActualTime(actual, true)
        setConfirmedPosition(actual)
      }
      previewTimerRef.current = window.setTimeout(pollPausedSeek, 100)
    }
    previewTimerRef.current = window.setTimeout(pollPausedSeek, 100)
    return () => {
      disposed = true
      if (previewTimerRef.current !== null) window.clearTimeout(previewTimerRef.current)
      previewTimerRef.current = null
    }
  }, [sampleActualTime, setConfirmedPosition, snapshot.actualTime, snapshot.status])

  useEffect(() => {
    onPlaybackStateChange?.({
      currentNodeId: snapshot.currentNodeId,
      playing: snapshot.status === 'playing',
      switching: snapshot.status === 'switching',
      status: snapshot.status,
      playbackIntent: snapshot.playbackIntent,
    })
  }, [onPlaybackStateChange, snapshot.currentNodeId, snapshot.playbackIntent, snapshot.status])

  useEffect(() => {
    onEditorBusyChange?.(Boolean(temporaryPreview || fineSeekTarget !== null))
  }, [fineSeekTarget, onEditorBusyChange, temporaryPreview])

  useEffect(() => {
    onEditorPreviewStateChange?.(temporaryPreview?.kind === 'connection'
      ? { sourceLabel: temporaryPreview.source.label, candidateLabel: temporaryPreview.candidate.label, phase: temporaryPreview.phase }
      : null)
  }, [onEditorPreviewStateChange, temporaryPreview])

  useEffect(() => {
    if (!onEditorSelectionRequestReady) return
    const request = (nodeId: string) => {
      const state = snapshotRef.current
      if ((state.status === 'playing' || state.status === 'buffering') && state.playbackIntent) seekToNode(nodeId, true)
    }
    onEditorSelectionRequestReady(request)
    return () => onEditorSelectionRequestReady(null)
  }, [onEditorSelectionRequestReady, seekToNode])

  useEffect(() => {
    let disposed = false
    let player: YTPlayer | null = null
    const host = containerRef.current
    setReady(false)
    editorPreviewAcceptingRef.current = false
    onEditorPreviewReadyChange?.(false)
    setError('')
    setThumbnailIndex(0)
    firstPlayingRef.current = false
    userGestureRef.current = false
    playEntryAttemptedRef.current = false
    playEntryIdRef.current = crypto.randomUUID()
    programEndedRef.current = false
    generationRef.current += 1
    commit({ ...createPlayerSnapshot(configRef.current), status: 'unloaded' })

    void loadYouTubeApi()
      .then((YT) => {
        if (disposed || !host) return
        const mount = document.createElement('div')
        host.replaceChildren(mount)
        player = new YT.Player(mount, {
          host: 'https://www.youtube-nocookie.com',
          videoId: configRef.current.video.videoId,
          playerVars: { controls: 1, playsinline: 1, rel: 0, origin: window.location.origin },
          events: {
            onReady: () => {
              if (disposed) return
              const duration = player?.getDuration() ?? 0
              if (Number.isFinite(duration) && duration > 0) onDurationReady?.(duration)
              setReady(true)
              editorPreviewAcceptingRef.current = playerRef.current === player && Boolean(playerRef.current)
              onEditorPreviewReadyChange?.(editorPreviewAcceptingRef.current)
              commit({ ...snapshotRef.current, status: 'cover' })
              if (autoStartOnMountRef.current) {
                // Playlist continuation is deliberately not a user gesture.
                // Its PLAYING event must therefore never create a play entry.
                const first = validPlaybackNodes(configRef.current.nodes, configRef.current.video.duration)[0]
                if (first) {
                  player?.seekTo(first.start, true)
                  // Let YouTube apply the cue/seek first.  Calling play in the
                  // same ready callback is not dependable for every embed and
                  // can be discarded before the new video becomes playable.
                  autoStartTimerRef.current = window.setTimeout(() => {
                    autoStartTimerRef.current = null
                    if (!disposed && player && playerRef.current === player) player.playVideo()
                  }, 0)
                }
              }
            },
            onStateChange: ({ data }) => {
              if (!playerRef.current) return
              const activePreview = temporaryPreviewRef.current
              if (fineSeekTargetRef.current !== null && data === 3 && temporarySeekingRef.current) return
              if (activePreview) {
                const transition = previewTransitionRef.current
                if (transition?.token === previewGenerationRef.current) {
                  if (
                    (transition.stage === 'pausing'
                      || transition.stage === 'seeking'
                      || transition.stage === 'transitioning'
                      || transition.stage === 'restoring')
                    && (data === -1 || data === 1 || data === 2 || data === 3 || data === 5)
                  ) return
                  if (transition.stage === 'starting') {
                    if (data === 1) {
                      transition.stage = 'playing'
                      temporarySeekingRef.current = false
                    }
                    if (data === -1 || data === 1 || data === 2 || data === 3 || data === 5) return
                  }
                  if (transition.stage === 'playing') {
                    if (data === 1) return
                    if (data === 2 && transition.internalPausePending) {
                      const actual = playerRef.current.getCurrentTime()
                      if (Math.abs(actual - transition.start) <= configRef.current.playbackSettings.seekToleranceSeconds) {
                        transition.internalPausePending = false
                        return
                      }
                    }
                  }
                }
                cancelTemporaryPreview()
              }
              const actual = playerRef.current.getCurrentTime()
              sampleActualTime(actual, true)
              if (data === 1) {
                if (userGestureRef.current && !playEntryAttemptedRef.current) {
                  playEntryAttemptedRef.current = true
                  userGestureRef.current = false
                  if (gestureExpiryRef.current !== null) window.clearTimeout(gestureExpiryRef.current)
                  onActualPlayRef.current?.(configRef.current.video.videoId, playEntryIdRef.current)
                }
                firstPlayingRef.current = true
                setError('')
                commit({ ...snapshotRef.current, status: 'playing', playbackIntent: true })
                window.queueMicrotask(startScheduler)
              } else if (data === 3) {
                stopScheduler()
                commit({ ...snapshotRef.current, status: 'buffering' })
              } else if (data === 2) {
                stopScheduler()
                commit({
                  ...snapshotRef.current,
                  status: firstPlayingRef.current ? 'paused' : 'ready',
                  playbackIntent: false,
                })
                if (firstPlayingRef.current) setConfirmedPosition(actual)
              } else if (data === 0) {
                handleProviderEnded(actual)
              } else if (data === 5 && firstPlayingRef.current) {
                commit({ ...snapshotRef.current, status: 'ready', playbackIntent: false })
              }
            },
            onError: ({ data }) => {
              setReady(false)
              editorPreviewAcceptingRef.current = false
              onEditorPreviewReadyChange?.(false)
              const failedPreview = temporaryPreviewRef.current
              clearTemporaryPreview(false)
              if (failedPreview?.kind === 'connection') setEditorPreviewError(failedPreview.phase === 2 ? '無法切換至候選段落，請再試一次。' : '無法開始試聽接法，請再試一次。')
              stopScheduler(true)
              const message = youtubeErrorMessage[data] ?? `YouTube 播放錯誤（${data}）`
              setError(message)
              commit({ ...snapshotRef.current, status: 'error', playbackIntent: false })
              onPlaybackError?.(message)
            },
            onAutoplayBlocked: () => {
              setReady(false)
              editorPreviewAcceptingRef.current = false
              onEditorPreviewReadyChange?.(false)
              cancelTemporaryPreview(false)
              stopScheduler()
              commit({ ...snapshotRef.current, status: 'autoplay-blocked', playbackIntent: false })
              onAutoplayBlockedRef.current?.()
            },
          },
        })
        playerRef.current = player
      })
      .catch((caught: Error) => {
        setReady(false)
        editorPreviewAcceptingRef.current = false
        onEditorPreviewReadyChange?.(false)
        setError(caught.message)
        commit({ ...snapshotRef.current, status: 'error', playbackIntent: false })
        onPlaybackError?.(caught.message)
      })

    return () => {
      disposed = true
      editorPreviewAcceptingRef.current = false
      onEditorPreviewReadyChange?.(false)
      generationRef.current += 1
      stopScheduler()
      clearSeekTimer()
      clearPreviewTimer()
      if (autoStartTimerRef.current !== null) window.clearTimeout(autoStartTimerRef.current)
      autoStartTimerRef.current = null
      fineSeekTargetRef.current = null
      clearTemporaryPreview(false)
      if (gestureExpiryRef.current !== null) window.clearTimeout(gestureExpiryRef.current)
      player?.destroy()
      host?.replaceChildren()
      if (playerRef.current === player) playerRef.current = null
    }
  }, [
    clearSeekTimer,
    clearPreviewTimer,
    cancelTemporaryPreview,
    clearTemporaryPreview,
    commit,
    config.video.videoId,
    handleProviderEnded,
    onDurationReady,
    onPlaybackError,
    onEditorPreviewReadyChange,
    sampleActualTime,
    setConfirmedPosition,
    startScheduler,
    stopScheduler,
  ])

  useEffect(() => {
    const registerIframeGesture = () => {
      const iframe = containerRef.current?.querySelector('iframe')
      if (iframe && document.activeElement === iframe) {
        userGestureRef.current = true
        if (gestureExpiryRef.current !== null) window.clearTimeout(gestureExpiryRef.current)
        gestureExpiryRef.current = window.setTimeout(() => { userGestureRef.current = false }, 1600)
      }
    }
    window.addEventListener('blur', registerIframeGesture)
    return () => window.removeEventListener('blur', registerIframeGesture)
  }, [])

  const chooseNext = (nodeId: string) => {
    const actual = playerRef.current?.getCurrentTime() ?? snapshotRef.current.actualTime
    const result = toggleQueue(snapshotRef.current, configRef.current, nodeId, actual)
    if (result.locked) {
      setLockedMessage('段落即將結束，下一段已鎖定。')
      return
    }
    setLockedMessage('')
    commit(result.snapshot)
    if (result.snapshot.status === 'playing') {
      const active = findPlaybackNodeAtTime(configRef.current.nodes, configRef.current.video.duration, actual)
      schedulerRef.current?.rebuild(
        active ? active.end - configRef.current.playbackSettings.endToleranceSeconds : null,
        Boolean(result.snapshot.queuedNodeId),
        timingModeRef.current,
      )
    }
  }

  const beginPausePreview = useCallback((after: boolean, trigger: HTMLElement) => {
    if (!playerRef.current || fineDraft === null || !isPreviewSeconds(previewSeconds) || temporaryPreviewRef.current || fineSeekTarget !== null) return
    const seconds = Number(previewSeconds)
    const duration = configRef.current.video.duration ?? 0
    const anchor = fineDraft
    const start = after ? anchor : Math.max(0, anchor - seconds)
    const end = after ? Math.min(duration, anchor + seconds) : anchor
    if (end <= start) return
    previewFocusRef.current = trigger
    setEditorPreviewError('')
    setEditorPreviewStatus('')
    playPreviewRange(start, end, { kind: 'pause', end, restorePosition: anchor })
  }, [fineDraft, fineSeekTarget, playPreviewRange, previewSeconds])

  const beginConnectionPreview = useCallback((source: PreviewNode, candidate: PreviewNode, seconds: number, trigger: HTMLElement) => {
    if (!editorPreviewAcceptingRef.current || !playerRef.current || temporaryPreviewRef.current || fineSeekTarget !== null || !Number.isFinite(source.end) || !Number.isFinite(candidate.end)) return false
    const firstEnd = source.end as number
    const secondEnd = Math.min(candidate.start + seconds, candidate.end as number)
    const firstStart = Math.max(source.start, firstEnd - seconds)
    if (firstEnd <= firstStart || secondEnd <= candidate.start) return false
    previewFocusRef.current = trigger
    setEditorPreviewError('')
    setEditorPreviewStatus('')
    playPreviewRange(firstStart, firstEnd, {
      kind: 'connection',
      source,
      candidate,
      secondStart: candidate.start,
      secondEnd,
      phase: 1,
    })
    const acceptedPreview = temporaryPreviewRef.current as TemporaryPreview | null
    return acceptedPreview?.kind === 'connection'
      && previewTransitionRef.current?.token === previewGenerationRef.current
  }, [fineSeekTarget, playPreviewRange])

  useEffect(() => {
    if (!onEditorPreviewRequestReady) return
    onEditorPreviewRequestReady(beginConnectionPreview)
    return () => onEditorPreviewRequestReady(null)
  }, [beginConnectionPreview, onEditorPreviewRequestReady])

  const retryApi = () => {
    apiReady = null
    window.location.reload()
  }

  const currentTime = snapshot.actualTime
  const remaining = currentNode ? currentNode.end - currentTime : 0
  const countdown = snapshot.status === 'playing' && remaining > 0 && remaining <= 5 ? Math.ceil(remaining) : null
  const progress = currentNode
    ? Math.max(0, Math.min(100, ((currentTime - currentNode.start) / (currentNode.end - currentNode.start)) * 100))
    : 0
  const statusMessage = snapshot.status === 'buffering'
    ? '正在緩衝…'
    : snapshot.status === 'switching' && currentNode
      ? `切換至「${currentNode.label}」…`
      : snapshot.status === 'ended'
        ? '已到最後一段，播放已停止。'
        : ''

  useEffect(() => {
    if (countdown !== null && !countdownAnnouncedRef.current) {
      countdownAnnouncedRef.current = true
      setCountdownAnnouncement('段落即將結束')
      return
    }
    if (countdown === null && (remaining > 5 || remaining <= 0 || snapshot.status !== 'playing')) {
      countdownAnnouncedRef.current = false
      setCountdownAnnouncement('')
    }
  }, [countdown, remaining, snapshot.status])

  const fallbackUrl = thumbnails[thumbnailIndex] ?? null
  const fineMinimum = confirmedPausedPosition === null ? 0 : Math.max(0, quantizeTenths(confirmedPausedPosition - 2))
  const fineMaximum = confirmedPausedPosition === null
    ? 0
    : Math.min(config.video.duration ?? confirmedPausedPosition + 2, quantizeTenths(confirmedPausedPosition + 2))
  const fineTicks = useMemo(() => {
    if (confirmedPausedPosition === null) return []
    return [fineMinimum, confirmedPausedPosition, fineMaximum]
  }, [confirmedPausedPosition, fineMaximum, fineMinimum])
  const previewBusy = Boolean(temporaryPreview || fineSeekTarget !== null)
  const previewValid = isPreviewSeconds(previewSeconds)
  const editorLayout = Boolean(onConfirmedPausedPosition)
  const showPrecisionPreview = editorLayout && snapshot.status === 'paused' && confirmedPausedPosition !== null
  const playerFrame = (
    <div className="player-frame">
      {!ready && !firstPlayingRef.current && (
        <button
          className="player-frame__poster"
          type="button"
          disabled={!error}
          onClick={error ? retryApi : undefined}
          aria-label={error
            ? `重新載入「${config.video.title || 'YouTube 影片'}」播放器`
            : `播放「${config.video.title || 'YouTube 影片'}」`}
        >
          {fallbackUrl && (
            <img
              src={fallbackUrl}
              alt=""
              onError={() => setThumbnailIndex((index) => Math.min(index + 1, thumbnails.length))}
            />
          )}
          <span>{error ? '播放器暫時無法載入' : '正在載入播放器…'}</span>
        </button>
      )}
      <div className="player-frame__embed" ref={containerRef} />
    </div>
  )
  const nextChoices = showNextChoices ? (
    <div className="player-grid player-grid--single">
      <div>
        <h3>選擇下一段</h3>
        {snapshot.status === 'autoplay-blocked' && <p className="player-grid__status-message" role="alert">瀏覽器需要你點一下「播放」才能開始。</p>}
        {!snapshot.queuedNodeId && naturalNext && (
          <p className="natural-next-note">未預選，將依時間順序自然播放至：{naturalNext.label}</p>
        )}
        {choices.length ? (
          <div className="node-buttons">
            {choices.map((choice) => {
              const queued = choice.node.id === snapshot.queuedNodeId
              const lyric = choice.node.openingLyric?.trim()
              return (
                <button
                  type="button"
                  className={queued ? 'node-button node-button--queued' : 'node-button'}
                  aria-pressed={queued}
                  onClick={() => chooseNext(choice.node.id)}
                  key={`${choice.from}-${choice.to}-${choice.order}`}
                >
                  <strong>{choice.node.label}</strong>
                  <small>{queued ? '已選為下一段' : choice.label || '可選下一段'}</small>
                  <small className="node-button__lyric">{lyric ? `開頭歌詞：${lyric}` : '尚未填寫開頭歌詞'}</small>
                </button>
              )
            })}
          </div>
        ) : <p className="muted">此段沒有可選路徑；有下一段時自然順播，最後一段停止。</p>}
        {lockedMessage && <p className="queued-note" role="status">{lockedMessage}</p>}
      </div>
    </div>
  ) : null
  const playerStatus = (
    <div className="player-status">
      <div className="player-status__summary">
        <span className="eyebrow">目前段落</span>
        <strong>{currentNode?.label ?? '無涵蓋段落'}</strong>
        {currentNode && <small className="player-status__time">{formatTime(currentTime)} / {formatTime(currentNode.end)}</small>}
        {statusMessage && <small className="player-status__state" role="status">{statusMessage}</small>}
        {error && <p className="player-status__message" role="alert">{error}。{error === 'YouTube Player API 未載入' && <button className="text-button" type="button" onClick={retryApi}>重新載入播放器</button>} 你仍可在 <a href={`https://www.youtube.com/watch?v=${config.video.videoId}`} target="_blank" rel="noreferrer">YouTube 開啟影片</a>。</p>}
      </div>
      {immersiveEnabled && <button className="immersive-entry" type="button" onClick={onImmersiveOpen} disabled={!onImmersiveOpen || Boolean(error)} aria-label={error ? '播放器暫時無法進入沉浸播放。' : '進入沉浸播放'}><svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="M8 3H3v5M16 3h5v5M21 16v5h-5M3 16v5h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg><span>進入沉浸播放</span></button>}
      <div className="segment-progress" role="progressbar" aria-label="目前段落進度" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)} aria-valuetext={currentNode ? `${currentNode.label}，${formatTime(currentTime)} / ${formatTime(currentNode.end)}` : '沒有目前段落'}><span style={{ width: `${progress}%` }} /></div>
    </div>
  )
  const wholeSongNavigator = (
    <WholeSongSegmentNavigator
      nodes={nodes}
      duration={config.video.duration ?? 0}
      actualTime={currentTime}
      currentNodeId={snapshot.currentNodeId}
      queuedNodeId={snapshot.queuedNodeId}
      status={snapshot.status}
      playbackIntent={snapshot.playbackIntent}
      visitedNodeIds={visitedNodeIds}
      countdown={countdown}
      disabled={(!ready && snapshot.status !== 'autoplay-blocked') || snapshot.status === 'unloaded' || snapshot.status === 'error' && !ready}
      error={error}
      onSelect={switchToNodeNow}
      onRetryPlayer={retryApi}
      youtubeVideoId={config.video.videoId}
      immersiveEnabled={immersiveEnabled}
      immersiveActive={immersiveActive}
      onImmersiveOpen={onImmersiveOpen}
    />
  )
  const precisionPreview = showPrecisionPreview && confirmedPausedPosition !== null ? (
    <section className="precision-preview" aria-labelledby="precision-preview-heading">
      <h3 id="precision-preview-heading">精細定位</h3>
      <p>影片位置 {formatTime(confirmedPausedPosition)} · 範圍 {formatTime(fineMinimum)}–{formatTime(fineMaximum)}</p>
      <div className="precision-preview__range">
        <div className="precision-preview__scale" aria-hidden="true">
          {fineTicks.map((tick, index) => <span key={`${tick}-${index}`}>{formatTime(tick)}</span>)}
        </div>
        <label className="sr-only" htmlFor="precision-position">精細定位</label>
        <input
          id="precision-position"
          type="range"
          min={fineMinimum}
          max={fineMaximum}
          step="0.1"
          value={fineDraft ?? confirmedPausedPosition}
          aria-valuemin={fineMinimum}
          aria-valuemax={fineMaximum}
          aria-valuenow={fineDraft ?? confirmedPausedPosition}
          aria-valuetext={`新增段落位置 ${formatTime(fineDraft ?? confirmedPausedPosition)}；影片位置 ${formatTime(confirmedPausedPosition)}`}
          aria-describedby="precision-candidate-help"
          onChange={(event) => { const target = quantizeTenths(Number(event.target.value)); setFineDraft(target); onConfirmedPausedPosition?.(target) }}
        />
        <strong>新增段落位置 {formatTime(fineDraft ?? confirmedPausedPosition)}</strong>
      </div>
      <p id="precision-candidate-help">拖曳只調整新增段落位置，不會移動影片。</p>
      <div className="precision-preview__actions">
        <label className="field precision-preview__duration">
          <span>預聽長度</span>
          <span className="input-suffix"><input type="number" min="0.5" max="10" step="0.1" inputMode="decimal" value={previewSeconds} disabled={previewBusy} aria-invalid={previewSecondsTouched && !previewValid} aria-describedby={previewSecondsTouched && !previewValid ? 'pause-preview-seconds-error' : undefined} onChange={(event) => { setPreviewSeconds(event.target.value); setPreviewSecondsTouched(true) }} onBlur={() => setPreviewSecondsTouched(true)} onKeyDown={(event) => { if (event.key === 'Enter') setPreviewSecondsTouched(true) }} /><span>秒</span></span>
          {previewSecondsTouched && !previewValid && <small id="pause-preview-seconds-error" className="field-error">請輸入 0.5 到 10.0 秒。</small>}
        </label>
        <button className="button" type="button" disabled={previewBusy || !previewValid} onClick={(event) => beginPausePreview(false, event.currentTarget)}>播放前 {previewValid ? Number(previewSeconds).toFixed(1) : previewSeconds} 秒</button>
        <button className="button" type="button" disabled={previewBusy || !previewValid} onClick={(event) => beginPausePreview(true, event.currentTarget)}>播放後 {previewValid ? Number(previewSeconds).toFixed(1) : previewSeconds} 秒</button>
      </div>
      {fineSeekTarget !== null && <p role="status">正在定位至 {formatTime(fineSeekTarget)}…</p>}
      {temporaryPreview?.kind === 'pause' && <p role="status">正在預聽：{formatTime(snapshot.actualTime)}–{formatTime(temporaryPreview.end)}</p>}
    </section>
  ) : null
  const editorFeedback = countdown !== null || countdownAnnouncement || editorPreviewStatus || editorPreviewError ? (
    <div className="editor-player-feedback">
      {countdown !== null && !immersiveActive && <p className="player-countdown" aria-label={`剩餘 ${countdown} 秒`}>{countdown}</p>}
      {countdownAnnouncement && <span className="sr-only" role="status">{countdownAnnouncement}</span>}
      {editorPreviewStatus && <p className="editor-preview-status" role="status">{editorPreviewStatus}</p>}
      {editorPreviewError && <p className="field-error" role="alert">{editorPreviewError}</p>}
    </div>
  ) : null

  return (
    <section
      className={`interactive-player${compact ? ' interactive-player--compact' : ''}${shareLayout ? ' interactive-player--share' : ''}${playlistPane ? ' interactive-player--playlist' : ''}${editorLayout ? ' interactive-player--editor' : ''}`}
      aria-label={nodes.length ? '互動式段落播放器' : 'YouTube 影片預覽'}
    >
      {playerFrame}
      {nodes.length > 0 && (
        <>
          {editorLayout ? (
            <div className="editor-player-info-shell">
              <div className={`editor-player-info${showPrecisionPreview ? ' editor-player-info--precision' : ''}`}>
                {playerStatus}
                {precisionPreview}
                {editorFeedback}
              </div>
            </div>
          ) : (
            <>
              {wholeSongNavigator}
              {countdownAnnouncement && <span className="sr-only" role="status">{countdownAnnouncement}</span>}
              {editorPreviewStatus && <p className="editor-preview-status" role="status">{editorPreviewStatus}</p>}
              {editorPreviewError && <p className="field-error" role="alert">{editorPreviewError}</p>}
            </>
          )}
          {playlistPane ? <div className="playlist-player-side">{nextChoices}{playlistPane}</div> : nextChoices}
        </>
      )}
      {error && !nodes.length && <div className="alert alert--danger" role="alert">{error}。{error === 'YouTube Player API 未載入' && <button className="text-button" type="button" onClick={retryApi}>重新載入播放器</button>} 你仍可在 <a href={`https://www.youtube.com/watch?v=${config.video.videoId}`} target="_blank" rel="noreferrer">YouTube 開啟影片</a>。</div>}
    </section>
  )
}
